import { HudDrawer, useHudDrawerSideLabel } from '@sorrem-client/ui/components';
import type { HudEdge, Vector2 } from '@sorrem/shared';
import { HudPanel } from '@sorrem-client/ui/components';
import { useMemo } from 'react';
import { useMiniMap } from './use-mini-map';
import styles from './MiniMapPanel.module.scss';

interface MiniMapPanelProps {
    open: boolean;
    title?: string;
    onOpenChange: (open: boolean) => void;
    /** The screen edge it slides off: the side of its HUD area (default right, #210). */
    edge?: HudEdge;
}

/** How much of the map the mini map shows across, in projected tile units (a tile is 2 wide). */
const VIEW_SPAN = 72;

/** Tile units to the mini map's plane, turned like the world view's 2:1 isometric camera. */
function toMiniMapPoint(point: Vector2): Vector2 {
    return { x: point.x - point.y, y: (point.x + point.y) / 2 };
}

function toPathPoints(points: readonly Vector2[]): string {
    return points.map((point) => {
        const projected = toMiniMapPoint(point);
        return `${projected.x},${projected.y}`;
    }).join('L');
}

/** Closed loops to one SVG path. */
function toPathData(loops: readonly (readonly Vector2[])[]): string {
    return loops.map((loop) => `M${toPathPoints(loop)}Z`).join('');
}

/** Open segments to one SVG path. */
function toSegmentData(segments: readonly (readonly Vector2[])[]): string {
    return segments.map((segment) => `M${toPathPoints(segment)}`).join('');
}

function Marker({ position, className, radius, fill }: { position: Vector2; className?: string; radius: number; fill?: string }): JSX.Element {
    const projected = toMiniMapPoint(position);
    return <circle className={className} cx={projected.x} cy={projected.y} r={radius} fill={fill} />;
}

/** The Crucible heart: a diamond at its tile, in tile units across. */
function HeartDiamond({ position, size }: { position: Vector2; size: number }): JSX.Element {
    const projected = toMiniMapPoint(position);
    const half = size / 2;
    return (
        <polygon
            className={styles.heart}
            points={`0,${-half} ${half},0 0,${half} ${-half},0`}
            transform={`translate(${projected.x} ${projected.y})`}
        />
    );
}

/** The local player's arrow, centered on the tile, pointing along +x before it is turned. */
const SELF_ARROW_POINTS = '2.2,0 -1.4,1.6 -0.6,0 -1.4,-1.6';

/** The local player: an arrow at their tile, turned to where they face on the mini map's plane. */
function SelfArrow({ position, facing }: { position: Vector2; facing: Vector2 }): JSX.Element {
    const projected = toMiniMapPoint(position);
    const direction = toMiniMapPoint(facing);
    const degrees = Math.atan2(direction.y, direction.x) * (180 / Math.PI);
    return (
        <polygon
            className={styles.self}
            points={SELF_ARROW_POINTS}
            transform={`translate(${projected.x} ${projected.y}) rotate(${degrees})`}
        />
    );
}

/** The known areas around the local player, with the party, enemies nearby, the heart and essence orbs. */
function MiniMap(): JSX.Element {
    const { floor, edges, self, selfFacing, allies, enemies, heart, essenceOrbs } = useMiniMap();
    const center = toMiniMapPoint(self);
    const viewBox = `${center.x - VIEW_SPAN / 2} ${center.y - VIEW_SPAN / 2} ${VIEW_SPAN} ${VIEW_SPAN}`;
    // The known floor only changes when an area becomes known; the markers move several times a second.
    const floorData = useMemo(() => toPathData(floor), [floor]);
    const wallData = useMemo(() => toSegmentData(edges.walls), [edges.walls]);
    const openingData = useMemo(() => toSegmentData(edges.openings), [edges.openings]);

    return (
        <svg className={styles.map} viewBox={viewBox} preserveAspectRatio="xMidYMid slice" role="img" aria-label="Mini map">
            {/* The known floor as one shape, so joined rooms share one outline; doorways onto rooms not yet known are faded. */}
            {floorData !== '' && <path className={styles.floor} d={floorData} fillRule="evenodd" />}
            {wallData !== '' && <path className={styles.wallEdge} d={wallData} />}
            {openingData !== '' && <path className={styles.openingEdge} d={openingData} />}
            {essenceOrbs.map((orb, index) => <Marker key={`orb-${index}`} position={orb.position} radius={0.45} fill={orb.color} />)}
            {enemies.map((enemy, index) => <Marker key={`enemy-${index}`} position={enemy} className={styles.enemy} radius={0.9} />)}
            {heart && <HeartDiamond position={heart} size={4.2} />}
            {allies.map((ally, index) => <Marker key={`ally-${index}`} position={ally} className={styles.ally} radius={1.2} />)}
            <SelfArrow position={self} facing={selfFacing} />
        </svg>
    );
}

/** The HUD's mini map: slides off its side's edge, leaving a side label to bring it back. */
export function MiniMapPanel({ open, title = "Map", onOpenChange, edge = 'right' }: MiniMapPanelProps): JSX.Element {
    const sideLabel = useHudDrawerSideLabel(edge, title, 'toggle-map');

    return (
        <HudDrawer edge={edge} open={open}>
            <HudPanel
                title={title}
                className={styles.panel}
                bodyClassName={styles.body}
                {...sideLabel}
                onSideLabelClick={() => onOpenChange(!open)}
            >
                <MiniMap />
            </HudPanel>
        </HudDrawer>
    );
}
